/**
 * Authentication utilities for VF ACTIONS
 * Centralized token management to prevent localStorage key inconsistencies
 */

const TOKEN_KEY = 'vf_token';
const PENDING_PROJECT_KEY = 'pending_project_id';

/**
 * Get the authentication token from localStorage
 * @returns {string|null} The JWT token or null if not authenticated
 */
export const getAuthToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Set the authentication token in localStorage
 * @param {string} token - The JWT token to store
 */
export const setAuthToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Remove the authentication token from localStorage (logout)
 */
export const removeAuthToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if a token exists
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Get authorization headers for API requests
 * @returns {Object} Headers object with Authorization if token exists
 */
export const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Get pending project ID (for demo to user conversion)
 * @returns {string|null} The pending project ID or null
 */
export const getPendingProjectId = () => {
  return localStorage.getItem(PENDING_PROJECT_KEY);
};

/**
 * Set pending project ID
 * @param {string} projectId - The project ID to store
 */
export const setPendingProjectId = (projectId) => {
  localStorage.setItem(PENDING_PROJECT_KEY, projectId);
};

/**
 * Remove pending project ID
 */
export const removePendingProjectId = () => {
  localStorage.removeItem(PENDING_PROJECT_KEY);
};

/**
 * Authenticated fetch wrapper
 * Automatically adds Authorization header if token exists
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const authFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
  
  return fetch(url, {
    ...options,
    headers
  });
};

export default {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  isAuthenticated,
  getAuthHeaders,
  getPendingProjectId,
  setPendingProjectId,
  removePendingProjectId,
  authFetch
};
